import type {
  ArrivalStopRole,
  DepartureLoadState,
  DvirPhase,
  RejectedCallType,
  TmsEvent,
  TmsEventCategory,
  TmsEventKind,
} from "@storyboard/web-client";

type EventOfKind<K extends TmsEventKind> = Extract<TmsEvent, { kind: K }>;

/** The `callRejection` kinds, named by kind: `other`'s untyped category keeps `category` from narrowing the union. */
const CALL_REJECTION_KINDS = [
  "notAtStop",
  "trailerMismatch",
  "callOutOfSequence",
  "noAutoDispatch",
  "dispatchLimitExceeded",
  "otherRejection",
] as const satisfies readonly TmsEventKind[];
const CALL_REJECTION_KIND_SET = new Set<TmsEventKind>(CALL_REJECTION_KINDS);

export type CallRejectionEvent = EventOfKind<(typeof CALL_REJECTION_KINDS)[number]>;
type CallRejectionV2Event = Extract<CallRejectionEvent, { version: 2 }>;

export function isCallRejection(event: TmsEvent): event is CallRejectionEvent {
  return CALL_REJECTION_KIND_SET.has(event.kind);
}

/** The chip's category segment: its label and the emoji that opens the chip. WEB-2277 adds color here. */
export type CategoryDisplay = { label: string; emoji: string };

/**
 * The chip's kind segment: its label and the one or two data elements that follow the time, primary first. A null or
 * undefined element is dropped from the chip. WEB-2277 adds the kind's icon here.
 */
export type KindDisplay<K extends TmsEventKind> = {
  label: string;
  data: (event: EventOfKind<K>) => readonly [primary?: string | null, secondary?: string | null];
};

/** Separator between the chip's segments. */
export const CHIP_SEPARATOR = " · ";

/** The emoji of a kind with no category, `other`, whose label is the vendor's own title. */
export const UNCATEGORIZED_EMOJI = "❔";

/** Per category, the label and emoji the catalog's Conventions assign. Total: a new category fails to compile. */
export const CATEGORY_DISPLAY: { [C in TmsEventCategory]: CategoryDisplay } = {
  call: { label: "Call", emoji: "📦" },
  callRejection: { label: "Call rejected", emoji: "⛔" },
  fuel: { label: "Fuel", emoji: "⛽" },
  inspection: { label: "Inspection", emoji: "🔍" },
  movement: { label: "Movement", emoji: "🚚" },
  routeAssignment: { label: "Route assignment", emoji: "🧭" },
  compliance: { label: "Compliance", emoji: "✅" },
  dutyStatus: { label: "Duty status", emoji: "🕒" },
  session: { label: "Session", emoji: "🔑" },
  specialDriving: { label: "Special driving", emoji: "🚗" },
  acknowledgement: { label: "Acknowledgement", emoji: "👍" },
  checkIn: { label: "Check-in", emoji: "📣" },
  equipment: { label: "Equipment", emoji: "🧰" },
  exception: { label: "Exception", emoji: "⚠️" },
  request: { label: "Request", emoji: "🙋" },
  training: { label: "Training", emoji: "🎓" },
  hoursOfService: { label: "Hours of service", emoji: "⏳" },
};

export const STOP_ROLE_LABEL: { [R in ArrivalStopRole]: string } = {
  pickup: "pickup",
  delivery: "delivery",
  stop: "stop",
};

export const LOAD_STATE_LABEL: { [S in DepartureLoadState]: string } = {
  loaded: "loaded",
  empty: "empty",
};

export const DVIR_PHASE_LABEL: { [P in DvirPhase]: string } = {
  preTrip: "pre-trip",
  postTrip: "post-trip",
};

export const REJECTED_CALL_TYPE_LABEL: { [T in RejectedCallType]: string } = {
  loadedCall: "Loaded call",
  emptyCall: "Empty call",
  arrival: "Arrival",
};

/**
 * `<stop> — <city>`, degrading to `<stop>` when the vendor sent no city, and to nothing when a row of a known kind
 * arrived without its stop: the chip drops the element rather than showing the hole.
 */
function location(stopName: string | null | undefined, city: string | null | undefined): string | null {
  if (!stopName) return null;
  return city ? `${stopName} — ${city}` : stopName;
}

function trailer(trailerNumber: string | null): string | null {
  return trailerNumber ? `trailer ${trailerNumber}` : null;
}

function gallons(amount: number | null): string | null {
  return amount == null ? null : `${Math.round(amount)} gal`;
}

function order(orderNumber: string | null): string | null {
  return orderNumber ? `order ${orderNumber}` : null;
}

function unit(vendorVehicleKey: string | null): string | null {
  return vendorVehicleKey ? `unit ${vendorVehicleKey}` : null;
}

function usd(amount: number | null): string | null {
  return amount == null ? null : `$${amount}`;
}

function hoursDriving(hours: number | null): string | null {
  return hours == null ? null : `${hours} h driving`;
}

/** The HOS clock a violation is against: `8-hour break`, `70-hour cycle`; nothing when the row carries no hours. */
function clock(clockHours: number | null | undefined, name: string): string | null {
  return clockHours == null ? null : `${clockHours}-hour ${name}`;
}

/** `Los Angeles, CA`, `Los Angeles`, or `CA`; null when the form carried neither. */
function cityState(city: string | null, state: string | null): string | null {
  const parts = [city, state].filter((part): part is string => Boolean(part));
  return parts.length === 0 ? null : parts.join(", ");
}

/** The non-zero OS&D counts as one element: `short 2, damaged 1`. */
function osdCounts(overCount: number | null, shortCount: number | null, damagedCount: number | null): string | null {
  const parts = [
    ["over", overCount],
    ["short", shortCount],
    ["damaged", damagedCount],
  ]
    .filter(([, count]) => typeof count === "number" && count > 0)
    .map(([name, count]) => `${name} ${count}`);
  return parts.length === 0 ? null : parts.join(", ");
}

/** The call a rejection refused: `Loaded call, load 1234`, `Empty call`; nothing when no header line named it. */
function callSummary(callType: RejectedCallType | null, loadNumber: string | null): string | null {
  if (callType === null) return null;
  const label = REJECTED_CALL_TYPE_LABEL[callType];
  return callType === "loadedCall" && loadNumber ? `${label}, load ${loadNumber}` : label;
}

/** `drop 5310, current —`: the dash marks the trailer the driver left blank; nothing when both are blank. */
function trailerPair(event: CallRejectionV2Event): string | null {
  const { dropTrailerNumber: drop, currentTrailerNumber: current } = event;
  if (drop === null && current === null) return null;
  return `drop ${drop ?? "—"}, current ${current ?? "—"}`;
}

/**
 * A version 1 rejection carries only the TMS's words for the refusal; a version 2 one leads with the call the driver
 * sent, followed by the kind's own detail.
 */
function rejectionData(
  event: CallRejectionEvent,
  detail: (event: CallRejectionV2Event) => string | null,
): readonly [string | null, string | null] {
  return event.version === 1
    ? [event.statusDesc, event.statusMsg]
    : [callSummary(event.callType, event.loadNumber), detail(event)];
}

function statusDesc(event: CallRejectionV2Event): string {
  return event.statusDesc;
}

type CallEvent = EventOfKind<"emptyCall" | "loadedCall">;
type CallV2Event = Extract<CallEvent, { version: 2 }>;

/**
 * How a version 2 call's trailer changed hands: `dropped 190104 → hooked 200288`, `dropped 200288, left bobtail`,
 * `live unload, trailer 140828`. A dash marks a number the driver left blank on a drop; with no exchange stated, the
 * trailer the truck left with stands alone.
 */
function exchangeText(event: CallV2Event): string | null {
  const { droppedTrailerNumber: dropped, departingTrailerNumber: departing } = event;
  switch (event.trailerExchange) {
    case "swapped":
      return `dropped ${dropped ?? "—"} → hooked ${departing ?? "—"}`;
    case "droppedOnly":
      return `dropped ${dropped ?? "—"}, left bobtail`;
    case "kept":
      if (event.kind === "loadedCall") return trailer(departing);
      return departing ? `live unload, trailer ${departing}` : "live unload";
    default:
      return trailer(departing);
  }
}

/** A version 1 call names one trailer; a version 2 one names both sides of the exchange. */
function callData(event: CallEvent): readonly [string | null, string | null] {
  return [
    location(event.stopName, event.city),
    event.version === 1 ? trailer(event.trailerNumber) : exchangeText(event),
  ];
}

/**
 * Per kind, the label and the chip data the catalog assigns. Total over TmsEventKind: a new kind fails to compile
 * until it is placed. `other` carries no fixed label; the chip shows the vendor's title in its place.
 */
export const KIND_DISPLAY: { [K in TmsEventKind]: KindDisplay<K> } = {
  emptyCall: { label: "Empty call", data: callData },
  loadedCall: { label: "Loaded call", data: callData },
  stopCall: { label: "Stop call", data: (e) => [location(e.stopName, e.city)] },
  fuelStop: { label: "Fuel stop", data: (e) => [e.stopName, gallons(e.gallons)] },
  dvir: { label: "DVIR", data: (e) => [DVIR_PHASE_LABEL[e.phase], e.stopName] },
  arrival: { label: "Arrival", data: (e) => [location(e.stopName, e.city), STOP_ROLE_LABEL[e.stopRole]] },
  departure: { label: "Departure", data: (e) => [location(e.stopName, e.city), LOAD_STATE_LABEL[e.loadState]] },
  routeAccepted: { label: "Route accepted", data: (e) => [order(e.orderNumber), e.isTeam ? "team" : null] },
  routeDeclined: { label: "Route declined", data: (e) => [order(e.orderNumber), e.isTeam ? "team" : null] },
  logCertified: { label: "Log certified", data: () => [] },
  driving: { label: "Driving", data: (e) => [e.withCoDriver ? "with co-driver" : null] },
  offDuty: { label: "Off duty", data: (e) => [e.withCoDriver ? "with co-driver" : null] },
  onDuty: { label: "On duty", data: (e) => [e.withCoDriver ? "with co-driver" : null] },
  sleeperBerth: { label: "Sleeper berth", data: (e) => [e.withCoDriver ? "with co-driver" : null] },
  login: { label: "Login", data: (e) => [unit(e.vendorVehicleKey)] },
  logout: { label: "Logout", data: (e) => [unit(e.vendorVehicleKey)] },
  personalConveyance: { label: "Personal conveyance", data: () => [] },
  specialDrivingEnded: { label: "Special driving ended", data: () => [] },
  yardMove: { label: "Yard move", data: () => [] },
  messageAcknowledged: { label: "Message acknowledged", data: (e) => [e.messageTitle] },
  verificationResponse: { label: "Verification response", data: (e) => [e.confirmation] },
  dailyCheckCall: { label: "Daily check call", data: (e) => [trailer(e.trailerNumber)] },
  dropSwapArrival: { label: "Drop/swap arrival", data: (e) => [e.dropLocation] },
  equipmentInventory: { label: "Equipment inventory", data: () => [] },
  accidentReport: { label: "Accident report", data: (e) => [e.location, e.hasInjuries ? "injuries" : null] },
  fuelCardError: { label: "Fuel card error", data: (e) => [e.fuelError] },
  loadRefused: { label: "Load refused", data: (e) => [order(e.orderNumber), e.refusalReason] },
  osdReport: {
    label: "OS&D report",
    data: (e) => [order(e.orderNumber), osdCounts(e.overCount, e.shortCount, e.damagedCount)],
  },
  tollPassIssue: { label: "Toll pass issue", data: () => [] },
  advanceRequest: {
    label: "Advance request",
    data: (e) => [usd(e.amountUsd), e.version === 2 ? e.reason : null],
  },
  fuelRequest: { label: "Fuel request", data: (e) => [cityState(e.city, e.state)] },
  loadHomeRequest: { label: "Load home request", data: (e) => [order(e.orderNumber)] },
  trainerDailyForm: { label: "Trainer daily form", data: (e) => [hoursDriving(e.drivingHours)] },
  trainingReport: { label: "Training report", data: (e) => [e.trainingType] },
  other: { label: "Other", data: () => [] },
  notAtStop: { label: "Not at stop", data: (e) => rejectionData(e, statusDesc) },
  trailerMismatch: { label: "Trailer mismatch", data: (e) => rejectionData(e, trailerPair) },
  callOutOfSequence: { label: "Call out of sequence", data: (e) => rejectionData(e, statusDesc) },
  noAutoDispatch: { label: "No auto-dispatch", data: (e) => rejectionData(e, trailerPair) },
  dispatchLimitExceeded: { label: "Dispatch limit exceeded", data: (e) => rejectionData(e, statusDesc) },
  otherRejection: { label: "Call rejected", data: (e) => rejectionData(e, statusDesc) },
  breakOverdue: { label: "Break overdue", data: (e) => [clock(e.clockHours, "break")] },
  cycleLimitExceeded: { label: "Cycle limit exceeded", data: (e) => [clock(e.clockHours, "cycle")] },
  drivingLimitExceeded: { label: "Driving limit exceeded", data: (e) => [clock(e.clockHours, "driving")] },
  dutyWindowExceeded: { label: "Duty window exceeded", data: (e) => [clock(e.clockHours, "window")] },
};

/** The kind's data elements for one event, with the nulls dropped. */
function dataOf<K extends TmsEventKind>(event: EventOfKind<K>): string[] {
  const display: KindDisplay<K> = KIND_DISPLAY[event.kind];
  return display.data(event).filter((element): element is string => Boolean(element));
}

/**
 * `<emoji> <Category> · <Kind> · <HH:MM> · <primary> [· <secondary>]`, nulls dropped, time in the viewer's zone.
 *
 * Every string is composed here from typed fields; nothing on the chip is server display text. An `other` event has
 * no category, so it reads `❔ <vendor title> · <HH:MM>`.
 */
export function tmsEventChipText(event: TmsEvent, formatTime: (iso: string) => string): string {
  const { emoji, text } = tmsEventChipParts(event, formatTime);
  return `${emoji} ${text}`;
}

/**
 * {@link tmsEventChipText} split at its emoji, for a layout that sets the emoji in a column of its own so wrapped text
 * lines up under the first line's.
 */
export function tmsEventChipParts(
  event: TmsEvent,
  formatTime: (iso: string) => string,
): { emoji: string; text: string } {
  if (event.kind === "other") {
    const segments = [event.label, formatTime(event.occurredAt), ...dataOf(event)];
    return { emoji: UNCATEGORIZED_EMOJI, text: segments.join(CHIP_SEPARATOR) };
  }
  const { emoji, label } = CATEGORY_DISPLAY[event.category];
  const segments = [label, KIND_DISPLAY[event.kind].label, formatTime(event.occurredAt), ...dataOf(event)];
  return { emoji, text: segments.join(CHIP_SEPARATOR) };
}
